/*
 * <cdx-avis-price mode="main|mirror"> – shows the price of the current Aris/AVIS configuration
 * (#avp-productoption-totalpriceadd, German format "1.579,71 €") in its first child element.
 *
 *   mirror  konfigurator sidebar price: follows every change right away
 *   main    product price (snippets/price.liquid with avis_price_sync): waits until AVIS has settled
 *           (350 ms without changes, at most 4 s), then follows changes. When the configured price
 *           differs from the first settled one, the bundle-sale badges and sale styling are removed.
 *           A price block re-rendered after a variant change picks the current price up on connect.
 */
if (!customElements.get("cdx-avis-price")) {
  const SOURCE_ID = "avp-productoption-totalpriceadd";
  const SETTLE_MS = 350;
  const MAX_WAIT_MS = 4000;
  const instances = new Set();

  const readAvisPrice = () => {
    const raw = document.getElementById(SOURCE_ID)?.textContent.trim();
    if (!raw || raw.length < 2) return null;
    // German format: dot = thousands, comma = decimals
    const cleaned = raw.replace(/[^0-9.,]/g, "").replace(/\./g, "").replace(",", ".");
    const value = parseFloat(cleaned);
    return Number.isNaN(value) || value <= 0 ? null : value;
  };

  const formatPrice = (value, separator) =>
    value.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".") + separator + "€";

  const clearBundleSale = () => {
    document.querySelectorAll(".cdx-bundle-sale-observed").forEach((el) => {
      el.style.display = "none";
    });
    const priceEl = document.querySelector(".price-bundle-special");
    if (priceEl) {
      priceEl.classList.remove("price-bundle-special", "price-item-sale", "price-item-last", "price-red");
      priceEl.style.color = "#4fb40d";
    }
  };

  const controller = {
    started: false,
    settled: false,
    baseline: null,

    start() {
      if (this.started) return;
      this.started = true;
      const source = document.getElementById(SOURCE_ID);
      if (source) {
        this.attach(source);
        return;
      }
      // AVIS renders its options after the page has loaded
      const waiter = new MutationObserver(() => {
        const span = document.getElementById(SOURCE_ID);
        if (!span) return;
        waiter.disconnect();
        this.attach(span);
      });
      waiter.observe(document.body, { childList: true, subtree: true });
    },

    attach(source) {
      this.maxTimer = setTimeout(() => this.markSettled(), MAX_WAIT_MS);
      new MutationObserver(() => this.onChange()).observe(source, {
        childList: true,
        characterData: true,
        subtree: true,
      });
      this.onChange();
    },

    onChange() {
      const price = readAvisPrice();
      if (price !== null) {
        instances.forEach((el) => {
          if (el.mode === "mirror") el.show(formatPrice(price, " "));
        });
      }
      if (this.settled) {
        this.applyMain();
        return;
      }
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.markSettled(), SETTLE_MS);
    },

    markSettled() {
      if (this.settled) return;
      this.settled = true;
      clearTimeout(this.settleTimer);
      clearTimeout(this.maxTimer);
      this.applyMain();
    },

    applyMain() {
      const mains = [...instances].filter((el) => el.mode === "main");
      const price = readAvisPrice();
      if (!mains.length || price === null) return;

      if (this.baseline === null) this.baseline = price;
      else if (Math.abs(price - this.baseline) > 0.005) clearBundleSale();

      mains.forEach((el) => el.show(formatPrice(price, " ")));
    },
  };

  customElements.define(
    "cdx-avis-price",
    class CdxAvisPrice extends HTMLElement {
      get mode() {
        return this.getAttribute("mode") === "mirror" ? "mirror" : "main";
      }

      connectedCallback() {
        instances.add(this);
        controller.start();
        if (this.mode === "main" && controller.settled) controller.applyMain();
        if (this.mode === "mirror") {
          const price = readAvisPrice();
          if (price !== null) this.show(formatPrice(price, " "));
        }
      }

      disconnectedCallback() {
        instances.delete(this);
      }

      show(text) {
        const target = this.firstElementChild || this;
        if (target.textContent.trim() !== text.trim()) target.textContent = text;
      }
    }
  );
}
